import {
  ApiError,
  ErrorCode,
  Role,
  type CompanyGroupResponse,
  type CompanyResponse,
  type UserResponse,
} from '../../api';

/**
 * What the people screens agree on: which roles exist, what they mean, how a
 * `409` from the create endpoint is read, and the words a person is shown and
 * an access is ended with.
 *
 * Its own module because there are three screens now -- the staff-wide list
 * ([`StaffUsersPage`](./StaffUsersPage.tsx)), the staff-wide creation form
 * ([`NewStaffUserPage`](./NewStaffUserPage.tsx)) and the panel on a company's
 * or a group's detail ([`UsersPanel`](./UsersPanel.tsx)) -- and a role label
 * or a confirmation spelled two ways across them is how a screen stops being
 * trusted: the second copy goes on passing its own tests while drifting away
 * from the screen next door. Same reason `onboarding.ts` and `certificates.ts`
 * exist.
 */

/** The four roles a person can hold. The machine roles are not among them. */
export const HUMAN_ROLES = [
  Role.VYTTECH_ADMIN,
  Role.VYTTECH_ANALYST,
  Role.CUSTOMER_ADMIN,
  Role.CUSTOMER_USER,
] as const;

export type HumanRole = (typeof HUMAN_ROLES)[number];

export const ROLE_LABELS: Record<HumanRole, string> = {
  [Role.VYTTECH_ADMIN]: 'Administração vyttech',
  [Role.VYTTECH_ANALYST]: 'Análise vyttech',
  [Role.CUSTOMER_ADMIN]: 'Administração do cliente',
  [Role.CUSTOMER_USER]: 'Usuário do cliente',
};

export const ROLE_OPTIONS = HUMAN_ROLES.map((role) => ({ value: role, label: ROLE_LABELS[role] }));

/** What each role is actually for, so nobody has to guess from the name. */
export const ROLE_MEANINGS: Record<HumanRole, string> = {
  [Role.VYTTECH_ADMIN]:
    'Equipe vyttech, com permissão de escrita — inclusive para criar e desativar pessoas nesta tela.',
  [Role.VYTTECH_ANALYST]:
    'Equipe vyttech, para trabalhar a fila de análise e os envios. Lê esta tela, mas não escreve nela.',
  [Role.CUSTOMER_ADMIN]: 'Responsável de RH na empresa cliente, no portal da própria empresa.',
  [Role.CUSTOMER_USER]: 'Pessoa da empresa cliente, no portal da própria empresa.',
};

/** A vyttech role belongs to no company; a customer role belongs to exactly one. */
export function isVyttechRole(role: HumanRole): boolean {
  return role === Role.VYTTECH_ADMIN || role === Role.VYTTECH_ANALYST;
}

/**
 * The e-mail address is already somebody's, and this screen has to say so
 * itself.
 *
 * `POST /api/v1/users` reports the collision as a bare `409` -- verified
 * against the running API -- with `details: []`, because it comes from the
 * database's unique index rather than from request validation. There is
 * therefore no field for `fieldErrors` to key on, and the generic copy
 * for `conflict` ("esta informação foi alterada por outra pessoa, recarregue")
 * describes a stale write, which this is not: nothing on this screen is out of
 * date and reloading changes nothing.
 *
 * What makes the mapping safe rather than a guess is that the endpoint has
 * exactly one uniqueness constraint a form can reach. `users.email` is the only
 * unique column on the table; the check constraint pairing role with company is
 * satisfied by the handler itself, and the company arrives from a select the
 * server populated. So a `409` from *this* request is this field.
 */
export const EMAIL_TAKEN_FIELD = 'Já existe uma conta com este e-mail.';
export const EMAIL_TAKEN_FORM =
  'Já existe uma conta com este e-mail. Uma conta desativada continua ocupando o endereço, ' +
  'então procure a pessoa na lista de pessoas cadastradas antes de criar outra.';

export function isEmailTaken(error: unknown): boolean {
  return error instanceof ApiError && error.code === ErrorCode.CONFLICT;
}

/**
 * The question asked before an access ends, wherever the action sits.
 *
 * Desativar tira a pessoa do ar na próxima requisição dela, então a tela
 * pergunta antes -- e pergunta a mesma coisa na lista da equipe e no painel da
 * empresa, porque é a mesma coisa que acontece.
 */
export function deactivationQuestion(email: string): string {
  return (
    `Desativar ${email}? A pessoa deixa de entrar e a sessão que ela já tiver ` +
    'cai na próxima requisição.'
  );
}

export function roleLabel(role: UserResponse['role']): string {
  return ROLE_LABELS[role as HumanRole] ?? role;
}

/**
 * Where a customer-role person is registered (#391): one company, as always, or
 * a whole company group, acting for whichever of its companies they name.
 *
 * Chosen at creation and never moved afterwards, so the form asks it once and
 * the list only reads it back.
 */
export type UserScope = 'company' | 'group';

export const SCOPE_OPTIONS: readonly { readonly value: UserScope; readonly label: string }[] = [
  { value: 'company', label: 'Empresa' },
  { value: 'group', label: 'Grupo' },
];

export const SCOPE_MEANINGS: Record<UserScope, string> = {
  company: 'A pessoa entra no portal de uma empresa e de mais nenhuma.',
  group:
    'A pessoa pode atuar em qualquer empresa que estiver no grupo — uma de cada vez — e passa a ' +
    'alcançar as que entrarem no grupo depois, perdendo as que saírem.',
};

/**
 * What the list says a person belongs to.
 *
 * `kind` keeps a group visibly different from a company in the same column --
 * the screen marks it with a badge -- because "Grupo Exemplo" and "Exemplo
 * Ltda" read alike, and the difference between them is how many companies the
 * person can reach.
 */
export interface UserBelonging {
  readonly kind: 'vyttech' | 'company' | 'group';
  readonly name: string;
}

export function userBelonging(
  user: UserResponse,
  companies: readonly CompanyResponse[],
  groups: readonly CompanyGroupResponse[],
): UserBelonging {
  const groupId = user.group_id ?? null;
  if (groupId !== null) {
    return {
      kind: 'group',
      name: groups.find((group) => group.id === groupId)?.name ?? groupId,
    };
  }
  if (user.company_id === null) {
    // Not "—": staff belonging to no company is a fact about them, not a gap.
    return { kind: 'vyttech', name: 'vyttech' };
  }
  return {
    kind: 'company',
    name: companies.find((company) => company.id === user.company_id)?.name ?? user.company_id,
  };
}

export const USERS_COPY = {
  title: 'Usuários',
  description:
    'Quem entra no produto: a equipe vyttech e as pessoas das empresas clientes. Credenciais ' +
    'de sistemas parceiros ficam na tela ao lado.',
  addPerson: 'Cadastrar pessoa',
  addPersonDescription:
    'A senha inicial é combinada fora daqui e trocada por quem recebe. Ela não é exibida de ' +
    'novo nem pode ser recuperada — uma senha perdida é redefinida, não consultada.',
  adding: 'Cadastrando…',
  // The two sentences a person form says about its own fields, wherever it is
  // shown: the staff-wide screen and the panel on a company's detail ask for
  // the same e-mail and the same initial password, under the same rules.
  emailHint: 'É por ele que a pessoa entra, e ele não se repete no produto inteiro.',
  passwordHint:
    'No mínimo 12 caracteres. Entregue por um canal combinado, nunca pelo mesmo e-mail do convite.',
  back: 'Voltar para a lista de pessoas',
  cancel: 'Cancelar',
  listTitle: 'Pessoas cadastradas',
  listDescription:
    'Inclusive as desativadas: quem podia entrar, e até quando, é uma pergunta que precisa de ' +
    'resposta depois do fato.',

  // --- The fields a person is asked for, and the words a row is read with ---
  //
  // The staff-wide screens and the panel on a company's detail ask for the same
  // four things and read back the same row, so they say it once here. Two
  // copies of a label are two labels as soon as somebody edits one of them.
  fullNameLabel: 'Nome completo',
  emailLabel: 'E-mail',
  passwordLabel: 'Senha inicial',
  profileLabel: 'Perfil',
  nameLabel: 'Nome',
  belongingLabel: 'Empresa ou grupo',
  groupBadge: 'Grupo',
  statusLabel: 'Situação',
  active: 'Ativo',
  // Desativar não é apagar: a linha fica, marcada. Dizer isso com a mesma
  // palavra nas duas listas é o que faz a marca significar a mesma coisa.
  inactive: 'Desativado',
  deactivate: 'Desativar',
  deactivated: 'Acesso desativado.',
  deactivateRefused: 'Não foi possível desativar este acesso',
} as const;
